import React, { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { ShoppingBag, MapPin, Search, Loader2, Truck, Sparkles, Wine, ArrowRight, AlertTriangle } from "lucide-react";

/**
 * Shop — vitrine des cartes & posters geoVINUM.
 * Les produits sont synchronisés en direct depuis notre atelier d'impression
 * via /api/shop/products. Si la connexion est indisponible, /api/shop/status
 * le détecte et on affiche un état "Catalogue indisponible" explicite plutôt
 * que de servir des produits fantômes.
 *
 * Click sur une carte → /shop/product/:id (sélection variante + ajout panier).
 */
export default function Shop() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get("search") || "");
  const { totals } = useCart();

  useEffect(() => {
    const s = searchParams.get("search");
    if (s) setQ(s);
  }, [searchParams]);

  useEffect(() => {
    Promise.all([
      api.get("/shop/products").then((r) => Array.isArray(r.data) ? r.data : []).catch(() => []),
      api.get("/shop/status").then((r) => r.data).catch(() => null),
    ]).then(([prods, st]) => {
      setProducts(prods);
      setStatus(st);
    }).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return products;
    const needle = q.toLowerCase();
    return products.filter((p) =>
      [p.name, ...(p.tags || [])].join(" ").toLowerCase().includes(needle)
    );
  }, [products, q]);

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-10 md:py-16" data-testid="shop-page">
      <div className="flex items-center justify-between mb-2">
        <span className="chip-pink inline-flex items-center gap-1.5">
          <ShoppingBag className="w-3 h-3" /> Boutique geoVINUM
        </span>
        <Link to="/shop/cart" className="relative inline-flex items-center gap-2 text-sm font-bold text-[#1F3A5F] hover:text-[#E5276A]"
          data-testid="shop-cart-link">
          <ShoppingBag className="w-4 h-4" /> Panier
          {totals.count > 0 && (
            <span className="absolute -top-1.5 -right-3 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E5276A] text-white text-[10px] font-bold flex items-center justify-center">
              {totals.count}
            </span>
          )}
        </Link>
      </div>
      <h1 className="font-display text-4xl md:text-5xl text-[#1F3A5F] mt-4 leading-[1.05]">
        Cartes & posters pour les amoureux du vin
      </h1>
      <p className="text-[#4A5568] mt-3 max-w-2xl">
        Cartes vignobles imprimées en haute qualité, livrées dans le monde entier.
        Bourgogne, Bordeaux, Champagne, monde entier — décorez votre cave ou offrez à un passionné.
      </p>

      {/* Bandeau bénéfices */}
      <div className="mt-6 grid sm:grid-cols-3 gap-3">
        <div className="rounded-xl border border-[#1F3A5F]/12 bg-white p-3 flex items-center gap-2.5">
          <Truck className="w-4 h-4 text-[#E5276A] shrink-0" />
          <span className="text-xs text-[#1F3A5F]"><b>Livraison mondiale</b> · impression locale</span>
        </div>
        <div className="rounded-xl border border-[#1F3A5F]/12 bg-white p-3 flex items-center gap-2.5">
          <Sparkles className="w-4 h-4 text-[#E5276A] shrink-0" />
          <span className="text-xs text-[#1F3A5F]"><b>Papier premium</b> · finition mate, encres pigmentaires</span>
        </div>
        <div className="rounded-xl border border-[#1F3A5F]/12 bg-white p-3 flex items-center gap-2.5">
          <Wine className="w-4 h-4 text-[#E5276A] shrink-0" />
          <span className="text-xs text-[#1F3A5F]"><b>Conçu par sommeliers</b> · données AOC à jour</span>
        </div>
      </div>

      {/* Search */}
      <div className="mt-8 max-w-md flex items-center gap-2 bg-white border border-[#1F3A5F]/15 focus-within:border-[#E5276A] rounded-xl px-4 py-3 shadow-sm">
        <Search className="w-4 h-4 text-[#8B95A1]" />
        <input
          data-testid="shop-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher une région, un thème…"
          className="flex-1 bg-transparent outline-none text-sm text-[#1F3A5F] placeholder:text-[#8B95A1]"
        />
      </div>

      {/* Grille */}
      {loading && (
        <div className="mt-12 flex items-center gap-2 text-[#8B95A1]" data-testid="shop-loading">
          <Loader2 className="w-5 h-5 animate-spin" /> Chargement des produits…
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="mt-12 py-12 text-center" data-testid="shop-empty">
          {status && status.connected === false ? (
            <div className="max-w-lg mx-auto rounded-2xl border-2 border-[#E5276A]/30 bg-[#FFF8FB] p-6">
              <AlertTriangle className="w-10 h-10 mx-auto text-[#E5276A] mb-3" />
              <h3 className="font-display text-xl text-[#1F3A5F]">Catalogue temporairement indisponible</h3>
              <p className="text-sm text-[#4A5568] mt-2">
                Notre boutique se synchronise en direct avec notre atelier d&apos;impression.
                La connexion est en cours de rétablissement — merci de revenir dans quelques minutes.
              </p>
            </div>
          ) : (
            <>
              <ShoppingBag className="w-10 h-10 mx-auto text-[#E5276A]/40 mb-3" />
              {products.length === 0 ? (
                <>
                  <p className="text-[#8B95A1]">Aucun produit disponible pour le moment.</p>
                  <p className="text-xs mt-2 text-[#8B95A1]">L&apos;équipe geoVINUM ajoute régulièrement de nouvelles cartes — revenez bientôt.</p>
                </>
              ) : (
                <p className="text-[#8B95A1]">Aucun produit ne correspond à « {q} ».</p>
              )}
            </>
          )}        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="shop-grid">
          {filtered.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      )}

      {/* FAQ courte */}
      <div className="mt-16 pt-10 border-t border-[#1F3A5F]/8">
        <h2 className="font-display text-2xl text-[#1F3A5F]">Questions fréquentes</h2>
        <div className="mt-5 grid md:grid-cols-2 gap-5">
          <FAQ q="Comment sont produites les cartes ?" a="Impression à la demande haute qualité dans notre réseau de 130+ ateliers répartis dans 32 pays. Votre carte est imprimée au plus près de chez vous, pour réduire l'empreinte carbone." />
          <FAQ q="Quels formats sont disponibles ?" a="A4, A3, A2, A1 selon les modèles. Le format et le prix exact apparaissent sur la fiche produit." />
          <FAQ q="Combien de temps pour la livraison ?" a="5 à 7 jours ouvrés en Europe, 7 à 14 jours dans le reste du monde. Suivi inclus." />
          <FAQ q="Vous personnalisez les cartes ?" a="Pour les domaines & cavistes Pro : oui, contactez-nous à hello@geovinum.io pour des cartes sur-mesure (votre logo, vos appellations)." />
        </div>
        <div className="mt-8 text-center">
          <Link to="/pro" className="inline-flex items-center gap-2 text-sm font-bold text-[#E5276A] hover:underline">
            Pro : carte personnalisée pour votre domaine →
          </Link>
        </div>
      </div>
    </div>
  );
}

function ProductCard({ product: p }) {
  return (
    <Link
      to={`/shop/product/${p.id}`}
      data-testid={`shop-card-${p.id}`}
      className="app-card overflow-hidden group block hover:-translate-y-0.5 transition-transform"
    >
      <div className="aspect-[4/5] relative bg-gradient-to-br from-[#FBF7F3] via-[#FCE4ED]/30 to-[#FBF7F3] overflow-hidden">
        {p.image ? (
          <img
            src={p.image}
            alt={p.name}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={(e) => { e.target.style.display = "none"; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <MapPin className="w-12 h-12 text-[#E5276A]/30" />
          </div>
        )}
        {p.price && (
          <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur shadow-sm text-[#E5276A] text-xs font-bold">
            {p.price}
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-display text-lg text-[#1F3A5F] leading-tight group-hover:text-[#E5276A] transition-colors line-clamp-2">
          {p.name}
        </h3>
        {p.tags?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {p.tags.slice(0, 3).map((t) => (
              <span key={t} className="text-[10px] font-bold text-[#1F3A5F]/60 uppercase tracking-wider">
                {t}
              </span>
            )).reduce((acc, el, i) => i === 0 ? [el] : [...acc, <span key={`s-${i}`} className="text-[10px] text-[#1F3A5F]/40">·</span>, el], [])}
          </div>
        )}
        <div className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#E5276A]">
          Voir le produit <ArrowRight className="w-3 h-3" />
        </div>
      </div>
    </Link>
  );
}

function FAQ({ q, a }) {
  return (
    <div className="rounded-2xl bg-[#FBF7F3] p-5">
      <h3 className="font-bold text-[#1F3A5F] text-sm mb-2">{q}</h3>
      <p className="text-sm text-[#4A5568] leading-relaxed">{a}</p>
    </div>
  );
}
